import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    Button,
    IconButton,
    Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';
import { PlacesTextField } from '../../../../components/groups-teams/shared/PlacesTextField';
import { GroupsTeamsService } from '../../../../services/groups-teams.service';
import { toast } from 'react-toastify';

interface CreateGroupModalProps {
    open: boolean;
    onClose: () => void;
}

const fieldLabelSx = {
    fontSize: '12px',
    fontWeight: 600,
    color: GT_COLORS.textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: '.5px',
};

const inputSx = { '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' } };

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({ open, onClose }) => {
    const [name, setName] = useState('');
    const [location, setLocation] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setSaving(true);
        await GroupsTeamsService.createGroup(
            {
                name: name.trim(),
                location: location.trim() || undefined,
            },
            (isValid, message) => {
                setSaving(false);
                if (isValid) {
                    toast.success('Group created successfully.');
                    handleClose();
                } else {
                    toast.error(message || 'Failed to create group.');
                }
            }
        );
    };

    const handleClose = () => {
        setName('');
        setLocation('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Create Group</Typography>
                <IconButton onClick={handleClose} size="small" sx={{ color: GT_COLORS.textFaint }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {/* Group Name */}
                <Box sx={{ mb: '14px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mb: '4px' }}>
                        <Typography sx={fieldLabelSx}>Group Name</Typography>
                        <Typography sx={{ fontSize: '11px', color: '#d32f2f', fontWeight: 500 }}>*</Typography>
                    </Box>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g., Behavioral Health, Skilled Nursing"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoFocus
                        sx={inputSx}
                    />
                </Box>

                {/* Location */}
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mb: '4px' }}>
                        <Typography sx={fieldLabelSx}>Location</Typography>
                        <Typography sx={{ fontSize: '11px', color: GT_COLORS.textFaint, fontWeight: 400, fontStyle: 'italic' }}>
                            (optional)
                        </Typography>
                    </Box>
                    <PlacesTextField value={location} onChange={setLocation} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        color: GT_COLORS.textMuted,
                        borderColor: GT_COLORS.borderInput,
                        textTransform: 'none',
                        fontSize: '14px',
                        borderRadius: '8px',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={!name.trim() || saving}
                    variant="contained"
                    sx={{
                        backgroundColor: GT_COLORS.primary,
                        textTransform: 'none',
                        fontSize: '14px',
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                    }}
                >
                    Create Group
                </Button>
            </DialogActions>
        </Dialog>
    );
};
